import { useFacilitySearchOptions } from '@/hooks/useFacilitySearchOptions'
import { MAX_FILENAME_LENGTH, MAX_PNG_SIZE, useUpload } from '@/hooks/useUpload'
import { useSelector } from 'react-redux'

import {
  useCreateFloorMutation,
  useCreateFloorPlanMutation,
} from '@/graphql/generated/hooks'
import {
  CreateFloorInput,
  FacilityType,
  UploadedFileType,
} from '@/graphql/generated/schemas'
import { selectMe } from '@/redux/me/meSlice'

import { removeExtension } from '../utils/utils'

export const useCreateFloor = () => {
  const me = useSelector(selectMe)
  const {
    facilityOptions,
    isFacilitiesLoading,
    isFacilitiesError,
    setFacilityName,
  } = useFacilitySearchOptions({ filter: { type: FacilityType.Normal } })
  const [createFloor] = useCreateFloorMutation()
  const [createFloorPlan] = useCreateFloorPlanMutation()
  const [upload] = useUpload()

  const create = async (input: CreateFloorInput, file: File) => {
    let fileId = null
    if (file) {
      fileId = await upload({
        file,
        fileType: UploadedFileType.FloorPlan,
        validationOptions: {
          maxFileSizeInBytes: MAX_PNG_SIZE,
          maxFileNameLength: MAX_FILENAME_LENGTH,
        },
      })
    }

    // Create Floor
    const { data: floorData } = await createFloor({
      variables: { input },
      refetchQueries: [
        'FacilitiesTable',
        'facilityFloorsDrawer',
        'floorsByFacilityIds',
      ],
    })
    if (!file) {
      return
    }

    // Create Floorplan
    const floorId = floorData?.createFloor?.floor?.id
    const name = removeExtension(file.name)
    const s3Value = `${me?.id}/${fileId}`
    await createFloorPlan({
      variables: { input: { floorId, name, s3Key: s3Value } },
      refetchQueries: [
        'FacilitiesTable',
        'facilityFloorsDrawer',
        'floorsByFacilityIds',
      ],
    })
  }

  return {
    isFacilitiesLoading,
    isFacilitiesError,
    facilityOptions,
    create,
    setFacilityName,
  }
}
